import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { SnCamera, SnLayer } from '@lib/api/entities';
import { CamerasServiceInterface } from '@lib/cameras';
import { FsMapCamerasServiceProvider, IFsMap } from '@lib/geo/core';
import { SnLiveUser } from '@lib/live-tracking/models';

@Injectable()
export class MapCamerasFacadeService<TMap extends IFsMap> extends FsMapCamerasServiceProvider<TMap> {
  constructor(
    private readonly provider: FsMapCamerasServiceProvider<TMap>,
    private readonly camerasService: CamerasServiceInterface,
  ) {
    super();
  }
  public get id(): string {
    return this.provider.id;
  }
  public get geospatialProvider(): string {
    return this.provider.geospatialProvider;
  }

  // TODO: Currently markers are Google Only. To prevent Google code in Facade type any is used.
  // Once markers are refactored, we can add the type here
  public get advancedMarkers(): any[] {
    return this.provider.advancedMarkers;
  }
  public get pendingCameras(): string[] {
    return this.camerasService.getPendingCameras();
  }

  public get clustering(): boolean {
    return this.camerasService.getClustering();
  }
  public get isAvailable(): boolean {
    return this.provider.isAvailable;
  }
  public get canChange(): boolean {
    return this.provider.canChange;
  }
  public get sentrySearchResults(): any[] {
    return this.provider.sentrySearchResults;
  }
  public get lprCameras(): any[] {
    return this.provider.lprCameras;
  }
  public get advancedClusters(): any[] {
    return this.provider.advancedClusters;
  }
  public get camerasParam(): string {
    return this.camerasService.getCamerasParam();
  }
  public get deselectedCameraGroupsParam(): string {
    return this.camerasService.getDeselectedCameraGroupsParam();
  }
  public set deselectedCameraGroupsParam(value: string) {
    this.camerasService.setDeselectedCameraGroupsParam(value);
  }
  public get activeLayerPopouts(): any[] {
    return this.provider.activeLayerPopouts;
  }
  public get cameraLayerObjects(): any[] {
    return this.provider.cameraLayerObjects;
  }
  public get followMarker(): any {
    return this.provider.followMarker;
  }
  public get advancedSkydioMarkers(): any[] {
    return this.provider.advancedSkydioMarkers;
  }

  public get hovered$(): Observable<SnCamera> {
    return this.camerasService.hovered$;
  }
  public get cameraSelected$(): Observable<SnCamera> {
    return this.camerasService.cameraSelected$;
  }
  public get selectedUsers$(): Observable<{ users: SnLiveUser[]; channelType: string }> {
    return this.camerasService.selectedUsers$;
  }
  public get axonCameraHovered$(): Observable<boolean> {
    return this.provider.axonCameraHovered$;
  }
  public get uavMouseOver$(): Observable<void> {
    return this.provider.uavMouseOver$;
  }
  public get uavMouseOut$(): Observable<void> {
    return this.provider.uavMouseOut$;
  }
  public get firstTwoClicked$(): Observable<void> {
    return this.camerasService.firstTwoClicked$;
  }
  // TODO: MatDialogRef<any> should be replaced with DrawingShapeCompleteDialogComponent however it is currently located in the dashboard app
  // Shape will become google/esri shapes once empty Google Shell is added
  public get shapeComplete$(): Observable<{ shape: any; dialogRef: MatDialogRef<any> }> {
    return this.provider.shapeComplete$;
  }
  public getMarkers(): unknown[] {
    return this.provider.getMarkers();
  }
  public onMapLoaded(map: TMap): void {
    return this.provider.onMapLoaded(map);
  }
  public addToMap(): void {
    return this.provider.addToMap();
  }
  public removeFromMap(): void {
    return this.provider.removeFromMap();
  }
  public getCamera(cameraId: string): Observable<SnCamera> {
    return this.camerasService.getCamera(cameraId);
  }
  public openFususInvestigate(): void {
    return this.provider.openFususInvestigate();
  }
  public openPopup(camera: SnCamera): void {
    return this.provider.openPopup(camera);
  }
  public addPolyline(camera: SnCamera, element: Element): void {
    return this.provider.addPolyline(camera, element);
  }
  public removePolyline(): void {
    return this.provider.removePolyline();
  }
  public clearPolylines(): void {
    return this.provider.clearPolylines();
  }
  public getCamerasParameter(params: string[]): void {
    this.camerasService.getCamerasParameter(params);
  }
  public setClustering(enabled: boolean): void {
    this.camerasService.setClustering(enabled);
  }
  public removeLine(): void {
    return this.provider.removeLine();
  }
  public cancelDocking(): void {
    return this.provider.cancelDocking();
  }
  public cameraSelected(camera: SnCamera): void {
    return this.provider.cameraSelected(camera);
  }
  public cameraHovered(camera: SnCamera): void {
    return this.provider.cameraHovered(camera);
  }
  public emitFirstTwoClicked(): void {
    return this.provider.emitFirstTwoClicked();
  }
  public registerViewTabsFirstTwoClick(click$: Observable<void>): void {
    return this.provider.registerViewTabsFirstTwoClick(click$);
  }
  public openPopout(
    layer: SnLayer,
    marker: unknown,
    fields: string[],
    cameraMarker: unknown,
  ): void {
    return this.provider.openPopout(layer, marker, fields, cameraMarker);
  }
  public updateCameraLabelVisibility(): void {
    return this.provider.updateCameraLabelVisibility();
  }
}
